import { Component, type ErrorInfo, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { Button, Text } from "react-native-paper";

import { headingProps } from "@/lib/heading-props";
import { reloadPage } from "@/lib/navigate";
import { useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";

/** The one bespoke measure: long error copy is hard to read at full width. */
const MESSAGE_MAX_WIDTH = 448;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * The fallback is a function component so it can read the theme with hooks —
 * the boundary class mounts inside `ThemeProvider` (routes/__root.tsx), so the
 * palette is always there. What it deliberately does NOT show is the error's
 * own message: internals-adjacent text has no business on a marketing page,
 * and the console already carries the real report for whoever debugs it.
 *
 * Recovery is a full reload rather than a state reset: remounting the same
 * broken tree re-throws instantly for a deterministic crash (a button that
 * appears to do nothing), while a reload re-runs the bundle and clears every
 * piece of state — which also heals the transient class.
 */
function ErrorFallback() {
  const theme = useAppTheme();

  return (
    <View role="main" style={styles.container}>
      <Text
        variant="titleLarge"
        {...headingProps(1)}
        style={{ color: theme.colors.onSurface }}
      >
        Something went wrong
      </Text>
      <Text
        variant="bodyMedium"
        style={[styles.message, { color: theme.colors.onSurfaceVariant }]}
      >
        Something went wrong while drawing this page. Reloading usually fixes
        it.
      </Text>
      <Button mode="contained" onPress={() => reloadPage()}>
        Reload the page
      </Button>
    </View>
  );
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // The only reporting the site has: the console, for whoever is debugging.
    console.error(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.lg,
    gap: spacing.sm,
  },
  message: {
    maxWidth: MESSAGE_MAX_WIDTH,
    textAlign: "center",
  },
});
